import { useEffect, useState } from 'react';
import { Button, Divider } from 'antd';
import { useForm } from 'react-hook-form';
import { useToasts } from 'react-toast-notifications';
import isEmpty from 'lodash/isEmpty';
import {
  getTimeSlotConfigurationDefaultValues,
  handleTimeSlotConfigurationSubmit,
  SingleTimeSlotConfiguration,
  SingleTimeSlotConfigurationHeader,
  TimeSlotConfigurationsDataType,
  TimeSlotConfigurationsSchema,
  TimeSlotsConfigurationFormProps,
} from '@modules/timetable';
import { DrawerFooter } from '@components/shared';
import { useMst } from '@models/Root';
import { yupResolver } from '@hookform/resolvers';

export const TimeSlotsConfigurationForm = ({
  daysOfWeek,
  setTimeSlotsConfigurationVisible,
}: TimeSlotsConfigurationFormProps) => {
  const [firstDayOfWeek, ...otherDaysOfWeek] = daysOfWeek;
  const { batch, timeSlotConfigurations, timelineItems, week } = useMst().timetable;

  const {
    applyToAll: defaultApplyToAllValue,
    timeSlotConfigurationDefaultValues,
  } = getTimeSlotConfigurationDefaultValues({
    timeSlotConfigurations,
    timelineItems,
    week,
    batchClassIds: batch?.batch_classes.map((batchClass) => batchClass.id),
  });

  const [applyToAll, setApplyToAll] = useState<boolean>(defaultApplyToAllValue);
  const { control, handleSubmit, errors } = useForm<TimeSlotConfigurationsDataType>({
    resolver: yupResolver(TimeSlotConfigurationsSchema),
    defaultValues: timeSlotConfigurationDefaultValues,
  });

  const { addToast } = useToasts();

  useEffect(() => {
    if (!isEmpty(errors)) {
      addToast('Error. Please re-check your form and try again.', {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  }, [errors, addToast]);

  // We load this component only if the batch is present inside it's parent. Therefore, this code will never be reached.
  // This is only to satisfy Mr Typescript
  if (!batch) {
    return <div />;
  }

  return (
    <div>
      <div>
        <SingleTimeSlotConfigurationHeader
          dayOfWeek={firstDayOfWeek}
          applyToAll={applyToAll}
          setApplyToAll={setApplyToAll}
        />
        <SingleTimeSlotConfiguration errors={errors} name={firstDayOfWeek.name} control={control} />
      </div>

      {!applyToAll &&
        otherDaysOfWeek.map((dayOfWeek) => (
          <div key={dayOfWeek.name}>
            <SingleTimeSlotConfigurationHeader
              dayOfWeek={dayOfWeek}
              applyToAll={applyToAll}
              setApplyToAll={setApplyToAll}
            />
            <SingleTimeSlotConfiguration errors={errors} name={dayOfWeek.name} control={control} />
          </div>
        ))}

      <Divider />

      <DrawerFooter>
        <Button
          htmlType="submit"
          onClick={() => {
            handleTimeSlotConfigurationSubmit({
              handleSubmit,
              applyToAll,
              addToast,
              batchId: batch.id,
              setTimeSlotsConfigurationVisible,
              saveAsDefault: true,
            });
          }}
        >
          Save as default & Apply
        </Button>
        &nbsp;&nbsp;
        <Button
          htmlType="submit"
          type="primary"
          onClick={() => {
            handleTimeSlotConfigurationSubmit({
              handleSubmit,
              applyToAll,
              addToast,
              batchId: batch.id,
              setTimeSlotsConfigurationVisible,
            });
          }}
        >
          Save & Apply
        </Button>
      </DrawerFooter>
    </div>
  );
};
